'use client';

import { c as _c } from "react/compiler-runtime";
import type { FileReviewDecision } from '@/lib/pr-v2/file-review-decisions';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { PrV2GroupHeaderArgs } from './pr-v2-group-section';
import { useIsPrV2DecisionLineOnScreen } from '../decisions/pr-v2-decision-visibility';
import { PrV2GroupNavigation, PrV2GroupReviewedControl } from './pr-v2-group-controls';
import { PrV2GroupDescription } from './pr-v2-group-description';
import { getPrV2GroupHeaderProps } from './pr-v2-group-section';
import { PrV2GroupingStatus, PrV2GroupMeta } from './pr-v2-left-group-header';
import { formatFileReviewDecisionLines } from '@/lib/pr-v2/file-review-decisions';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
import { navigateToFileAndLine } from '@/lib/symbols/navigate-to-symbol';
import { cn } from '@/lib/utils';

// Short, so the card keeps up when a scroll passes several groups in a row.
const GROUP_SWAP_TRANSITION = {
  duration: 0.18,
  ease: [0.2, 0, 0, 1]
} as const;
const GROUP_SWAP_OFFSET_PX = 8;
// The card lists its decisions under the explanation, so its stats row leaves them out.
const NO_DECISIONS: readonly FileReviewDecision[] = [];

/**
 * One question for the reviewer. Its bar turns full orange while its line is on screen, so the
 * card shows which question the code in view is about.
 */
function PrV2GroupCardDecision(t0) {
  const $ = _c(20);
  const {
    decision,
    groupId
  } = t0;
  const navigateToFile = useDiffNavigationStore(_temp);
  const isOnScreen = useIsPrV2DecisionLineOnScreen(decision.filename, decision.lineStart);
  let t1;
  if ($[0] !== decision.filename) {
    t1 = decision.filename.split("/").pop() ?? decision.filename;
    $[0] = decision.filename;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const basename = t1;
  let t2;
  if ($[2] !== decision.filename || $[3] !== decision.lineStart || $[4] !== groupId || $[5] !== navigateToFile) {
    t2 = () => {
      if (decision.lineStart === null) {
        navigateToFile(decision.filename, {
          expandFile: true,
          source: "programmatic"
        });
        return;
      }
      navigateToFileAndLine(decision.filename, decision.lineStart, navigateToFile, "new", groupId);
    };
    $[2] = decision.filename;
    $[3] = decision.lineStart;
    $[4] = groupId;
    $[5] = navigateToFile;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const showInDiff = t2;
  const t3 = isOnScreen ? "border-orange-400 text-zinc-300" : "border-orange-400/40 text-zinc-400";
  let t4;
  if ($[7] !== t3) {
    t4 = cn("border-l-2 pl-2.5 text-[12px] leading-normal transition-colors duration-200", t3);
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== basename || $[10] !== decision.lineEnd || $[11] !== decision.lineStart) {
    t5 = decision.lineStart === null ? basename : `${basename} · ${formatFileReviewDecisionLines(decision.lineStart, decision.lineEnd)}`;
    $[9] = basename;
    $[10] = decision.lineEnd;
    $[11] = decision.lineStart;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== showInDiff || $[14] !== t5) {
    t6 = <button type="button" onClick={showInDiff} className="block rounded-sm text-[11px] text-zinc-500 underline decoration-zinc-600 decoration-dotted underline-offset-[3px] hover:text-zinc-300 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-zinc-600">{t5}</button>;
    $[13] = showInDiff;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== decision.title || $[17] !== t4 || $[18] !== t6) {
    t7 = <li className={t4}>{decision.title}{t6}</li>;
    $[16] = decision.title;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

/** 1 after a move to a later group, -1 after a move to an earlier one, 0 before the first move. */
function _temp(state) {
  return state.navigateToFile;
}
function useGroupSwapDirection(groupId, sectionIndex) {
  const $ = _c(3);
  let t0;
  if ($[0] !== groupId || $[1] !== sectionIndex) {
    t0 = {
      groupId,
      sectionIndex,
      direction: 0
    };
    $[0] = groupId;
    $[1] = sectionIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [shown, setShown] = useState(t0);
  if (shown.groupId === groupId) {
    return shown.direction;
  }
  const direction = sectionIndex < shown.sectionIndex ? -1 : 1;
  setShown({
    groupId,
    sectionIndex,
    direction
  });
  return direction;
}

/** Follows the element's height, so the card can ease between groups of different lengths. */
function useObservedHeight() {
  const $ = _c(4);
  const ref = useRef(null);
  const [height, setHeight] = useState("auto");
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const element = ref.current;
      if (!element) {
        return;
      }
      const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
      observer.observe(element);
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== height) {
    t2 = [ref, height];
    $[2] = height;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2 as const;
}
export function PrV2GroupCard(args) {
  const $ = _c(21);
  const {
    sectionIndex,
    sectionCount,
    groupId,
    groupName,
    groupNameReason,
    files,
    decisions,
    isGroupingPending,
    isGroupReviewed,
    onToggleGroupReviewed,
    onNavigatePrevious,
    onNavigateNext
  } = getPrV2GroupHeaderProps(args);
  const direction = useGroupSwapDirection(groupId, sectionIndex);
  const isReducedMotion = useReducedMotion() === true;
  const [contentRef, contentHeight] = useObservedHeight();
  let t0;
  if ($[0] !== direction || $[1] !== isReducedMotion) {
    t0 = direction === 0 ? false : {
      opacity: 0,
      x: isReducedMotion ? 0 : direction * GROUP_SWAP_OFFSET_PX
    };
    $[0] = direction;
    $[1] = isReducedMotion;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 1,
      x: 0
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0) {
    t2 = {
      initial: t0,
      animate: t1,
      transition: GROUP_SWAP_TRANSITION
    };
    $[4] = t0;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const swapMotion = t2;
  const t3 = "pr-v2-group-card";
  const t4 = "flex flex-col gap-1";
  const t5 = isGroupingPending ? null : <div className="flex items-center justify-between gap-3 text-[12px]"><PrV2GroupMeta sectionIndex={sectionIndex} sectionCount={sectionCount} showIndex={false} files={files} decisions={NO_DECISIONS} /><PrV2GroupReviewedControl sectionIndex={sectionIndex} groupName={groupName} isGroupReviewed={isGroupReviewed} onToggleGroupReviewed={onToggleGroupReviewed} /></div>;
  const t6 = motion;
  const t7 = "-m-1 overflow-hidden";
  const t8 = false;
  let t9;
  if ($[6] !== contentHeight) {
    t9 = {
      height: contentHeight
    };
    $[6] = contentHeight;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== isReducedMotion) {
    t10 = isReducedMotion ? {
      duration: 0
    } : GROUP_SWAP_TRANSITION;
    $[8] = isReducedMotion;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = "p-1";
  const t12 = isGroupingPending ? <motion.div key={groupId} {...swapMotion}><PrV2GroupingStatus sectionIndex={sectionIndex} fileCount={files.length} /></motion.div> : <><div className="flex items-start justify-between gap-3"><motion.h2 key={groupId} {...swapMotion} className="min-w-0 text-[14px] font-medium leading-6 text-zinc-50">{groupName}</motion.h2>{sectionCount > 1 ? <PrV2GroupNavigation onNavigatePrevious={onNavigatePrevious} onNavigateNext={onNavigateNext}><span className="min-w-8 text-center text-[12px] tabular-nums text-zinc-500"><span aria-hidden="true">{`${sectionIndex}/${sectionCount}`}</span><span className="sr-only">{`Group ${sectionIndex} of ${sectionCount}`}</span></span></PrV2GroupNavigation> : null}</div><motion.div key={groupId} {...swapMotion} className="mt-1"><PrV2GroupDescription groupNameReason={groupNameReason} />{decisions.length > 0 ? <ul aria-label="Decisions" className="mt-3 space-y-2.5">{decisions.map(decision => <PrV2GroupCardDecision key={`${decision.filename}:${decision.lineStart ?? "file"}:${decision.title}`} decision={decision} groupId={groupId} />)}</ul> : null}</motion.div></>;
  let t13;
  if ($[10] !== contentRef || $[11] !== t12) {
    t13 = <div ref={contentRef} className={t11}>{t12}</div>;
    $[10] = contentRef;
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== t10 || $[14] !== t13 || $[15] !== t6.div || $[16] !== t9) {
    t14 = <t6.div className={t7} initial={t8} animate={t9} transition={t10}>{t13}</t6.div>;
    $[13] = t10;
    $[14] = t13;
    $[15] = t6.div;
    $[16] = t9;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t14 || $[19] !== t5) {
    t15 = <div data-testid={t3} className={t4}>{t5}{t14}</div>;
    $[18] = t14;
    $[19] = t5;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  return t15;
}